import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Check, Loader2, Search, X, AlertCircle } from 'lucide-react';
import { Customer } from '../../types';
import { fetchCustomers } from '../../services/crmService';
import { describeError } from '../../services/errorMessage';
import { rankCustomerCandidates } from '../project-hub/customerLinkLogic';

/**
 * Gemeinsamer Kunden-Picker.
 *
 * Die Auswahl eines Kunden fuer ein Projekt taucht an zwei Stellen auf: in den
 * Projekt-Stammdaten (ProjectCustomerLink) und beim Anlegen einer Rechnung fuer
 * ein Projekt ohne Kunden (InvoicesView). Beide teilen sich diese Bausteine —
 * die Rangfolge selbst liegt als pure Logik in project-hub/customerLinkLogic.
 *
 * `CustomerPickerPanel` ist der Suchkopf samt Trefferliste und passt in ein
 * Dropdown wie in einen Dialog; `CustomerPickerDialog` ist die modale Huelle
 * darum. Das Schreiben uebernimmt bewusst der Aufrufer (setProjectCustomer),
 * weil nur er weiss, was nach der Zuweisung weitergehen soll.
 */

export interface CustomerPickerPanelProps {
    /** Auswahl eines Kunden. Speichern und Fehlerbehandlung liegen beim Aufrufer. */
    onPick: (customer: Customer) => void;
    /**
     * Bereits geladene Kundenliste. Fehlt sie (undefined), laedt das Panel beim
     * Montieren selbst — so bleibt der Ladevorgang an das Oeffnen gekoppelt.
     */
    customers?: Customer[];
    /** Auftraggeber-Name aus den Projekt-Metadaten (metadata.customerName). */
    contextCustomerName?: string;
    /** Bauherr-Name aus den Projekt-Metadaten (metadata.clientName). */
    contextClientName?: string;
    /** Bereits verknuepfter Kunde — bleibt sichtbar und wird abgehakt. */
    currentCustomerId?: string | null;
    /** Waehrend des Speicherns: keine zweite Auswahl annehmen. */
    disabled?: boolean;
    autoFocus?: boolean;
    /** Hoehe der Trefferliste — im Dropdown knapper als im Dialog. */
    listMaxHeightClass?: string;
}

export const CustomerPickerPanel: React.FC<CustomerPickerPanelProps> = ({
    onPick,
    customers,
    contextCustomerName,
    contextClientName,
    currentCustomerId = null,
    disabled = false,
    autoFocus = true,
    listMaxHeightClass = 'max-h-64',
}) => {
    const selfLoad = customers === undefined;

    const [query, setQuery] = useState('');
    const [loaded, setLoaded] = useState<Customer[] | null>(null);
    // Ohne mitgelieferte Liste startet das Panel im Ladezustand — sonst blitzte
    // fuer einen Frame "Keine Kunden gefunden" auf.
    const [loading, setLoading] = useState(selfLoad);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        if (!selfLoad) return;
        let cancelled = false;
        fetchCustomers()
            .then((list) => { if (!cancelled) setLoaded(list); })
            .catch((e) => { if (!cancelled) setLoadError(describeError(e)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [selfLoad]);

    const contextNames = useMemo(
        () => [contextCustomerName, contextClientName],
        [contextCustomerName, contextClientName],
    );

    const candidates = useMemo(
        () => rankCustomerCandidates(customers ?? loaded ?? [], {
            query, contextNames, currentCustomerId, limit: 60,
        }),
        [customers, loaded, query, contextNames, currentCustomerId],
    );

    return (
        <>
            <div className="p-2.5 border-b border-slate-100 space-y-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        autoFocus={autoFocus}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Name oder Kundennummer suchen…"
                        className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                </div>
                {(contextCustomerName || contextClientName) && (
                    <p className="text-[11px] text-slate-500 px-1 leading-relaxed">
                        Laut Projekt-Stammdaten:
                        {contextCustomerName && <> Auftraggeber <strong className="text-slate-700">{contextCustomerName}</strong></>}
                        {contextCustomerName && contextClientName && ' ·'}
                        {contextClientName && <> Bauherr <strong className="text-slate-700">{contextClientName}</strong></>}
                    </p>
                )}
            </div>
            <div className={`${listMaxHeightClass} overflow-y-auto py-1`}>
                {loading ? (
                    <div className="px-4 py-6 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Kunden werden geladen…
                    </div>
                ) : loadError ? (
                    <div className="px-4 py-6 text-center text-xs text-red-600">{loadError}</div>
                ) : candidates.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-slate-400">Keine Kunden gefunden</div>
                ) : (
                    candidates.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => onPick(c)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50/60 transition-colors group disabled:opacity-50"
                        >
                            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                <Briefcase className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-slate-800 truncate">{c.name || 'Unbenannter Kunde'}</div>
                                <div className="text-[11px] text-slate-400 truncate">
                                    {[c.customerNumber, c.city || c.address].filter(Boolean).join(' · ') || '—'}
                                </div>
                            </div>
                            {c.id === currentCustomerId && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                        </button>
                    ))
                )}
            </div>
        </>
    );
};

export interface CustomerPickerDialogProps extends CustomerPickerPanelProps {
    title?: string;
    /** Erklaerung ueber der Suche — warum die Zuweisung jetzt noetig ist. */
    intro?: React.ReactNode;
    onClose: () => void;
    /** Laeuft die Zuweisung gerade? Dann bleibt der Dialog stehen und sperrt. */
    saving?: boolean;
    /** Fehler der Zuweisung, vom Aufrufer beschrieben (describeError). */
    error?: string | null;
}

export const CustomerPickerDialog: React.FC<CustomerPickerDialogProps> = ({
    title = 'Kunde zuweisen',
    intro,
    onClose,
    saving = false,
    error = null,
    ...panelProps
}) => (
    // z-[120]: zwei Stufen ueber der Modalebene z-[100]. Der Dialog wird aus dem
    // Anlagedialog heraus geoeffnet und liegt deshalb auf ihm.
    <div className="fixed inset-0 z-[120] bg-slate-900/40 flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-600" /> {title}
                </h3>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {intro && (
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 text-xs leading-relaxed text-slate-600">
                    {intro}
                </div>
            )}

            {error && (
                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border-b border-red-100 px-5 py-3">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <CustomerPickerPanel {...panelProps} disabled={saving || panelProps.disabled} />

            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400">
                    {saving ? 'Kunde wird zugewiesen…' : 'Die Zuweisung gilt für das Projekt, nicht nur für diesen Beleg.'}
                </span>
                <div className="flex items-center gap-2">
                    {saving && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50"
                    >
                        Abbrechen
                    </button>
                </div>
            </div>
        </div>
    </div>
);
